import React, { useCallback, useEffect, useMemo, useState } from "react";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import { menuItemSx } from "lib/muiStyle/muiStyle";
import { useRoleOptionsCache } from "utils/optionsCache/roleOptionsCache";

const RoleIdSearchField = ({
  label,
  value,
  onChangeValue,
  currentUserRole,
  disabled = false,
  required = false,
  error = false,
  errorMessage,
  sx,
}) => {
  const { fetchRoleOptions } = useRoleOptionsCache();
  const [selectedOption, setSelectedOption] = useState(null);
  const [preloadedOptions, setPreloadedOptions] = useState([]);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);

  const fetchOptions = useCallback(async (inputValue) => {
    const nextState = await fetchRoleOptions(inputValue, currentUserRole);
    setHasMoreOptions(nextState.hasMoreOptions);

    if (!`${inputValue || ""}`.trim()) {
      setPreloadedOptions(nextState.options);
    }

    return nextState.options;
  }, [currentUserRole]);

  useEffect(() => {
    if (disabled) return;
    fetchOptions("");
  }, [disabled, fetchOptions]);

  const normalizedValue = useMemo(() => `${value || ""}`.trim(), [value]);

  useEffect(() => {
    if (!normalizedValue) {
      setSelectedOption(null);
      return;
    }

    const matchedOption = preloadedOptions.find(
      (option) => `${option?.value ?? ""}` === normalizedValue,
    );
    setSelectedOption(
      matchedOption || {
        key: normalizedValue,
        value: normalizedValue,
        title: normalizedValue,
      },
    );
  }, [normalizedValue, preloadedOptions]);

  const handleChange = (event, newValue) => {
    setSelectedOption(newValue);
    onChangeValue?.(newValue?.value || "");
  };

  return (
    <div>
      <AsyncAutocomplete
        label={label}
        required={required}
        fetchOptions={fetchOptions}
        preloadedOptions={preloadedOptions}
        hasMoreOptions={hasMoreOptions}
        useLocalFilter
        numericExactMatch
        value={selectedOption}
        onChange={handleChange}
        getOptionLabel={(option) => option?.title || ""}
        isOptionEqualToValue={(option, selected) =>
          `${option?.value ?? ""}` === `${selected?.value ?? ""}`
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          const optionStyle = menuItemSx()["&.MuiMenuItem-root"];

          return (
            <li
              key={key}
              {...optionProps}
              style={{ ...optionProps.style, ...optionStyle }}
            >
              {option?.title || ""}
            </li>
          );
        }}
        disabled={disabled}
        error={error}
        sx={sx}
      />
      <HelperMessage message={error ? errorMessage : ""} />
    </div>
  );
};

export default React.memo(RoleIdSearchField);
